// src/phase4/systems/WaveManager.ts
// Wave management system for tower defense

import Phaser from 'phaser';
import type { WaveConfig, WaveStatus, EnemySpawn, WaveSummary, WaveRewards } from '../types/wave';
import type { EnemyInstance } from '../types/enemy';
import { generateExtendedWaveConfig, WAVE_CONFIG_SET } from '../data/wave-configs';
import { ENEMY_CONFIGS, getEnemyConfigWithDifficulty } from '../data/enemy-configs';

export interface SpawnCallback {
  (enemy: EnemyInstance, x: number, y: number): void;
}

export interface WaveCompleteCallback {
  (summary: WaveSummary): void;
}

export interface WaveAllCompleteCallback {
  (): void;
}

export class WaveManager {
  private scene: Phaser.Scene;
  private waveConfigs: WaveConfig[];
  private totalWaves: number;

  // State
  private currentWave: number = 0;
  private isWaveActive: boolean = false;
  private enemiesSpawned: number = 0;
  private currentSpawnIndex: number = 0;
  private spawnTimer: number = 0;
  private waveStartDelay: number = 0;

  // Tracking
  private activeEnemies: Map<string, EnemyInstance> = new Map();
  private spawnedEnemyIds: string[] = [];
  private totalEnemiesInWave: number = 0;
  private totalDamageDealt: number = 0;
  private enemiesKilled: number = 0;
  private totalEnemiesKilled: number = 0; // Cumulative kill count across all waves
  private enemiesEscaped: number = 0;

  // Callbacks
  private onSpawn?: SpawnCallback;
  private onWaveComplete?: WaveCompleteCallback;
  private onAllWavesComplete?: WaveAllCompleteCallback;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.waveConfigs = WAVE_CONFIG_SET.waves;
    this.totalWaves = WAVE_CONFIG_SET.totalWaves;
  }

  // Callback setters
  setSpawnCallback(callback: SpawnCallback): void {
    this.onSpawn = callback;
  }

  setWaveCompleteCallback(callback: WaveCompleteCallback): void {
    this.onWaveComplete = callback;
  }

  setAllWavesCompleteCallback(callback: WaveAllCompleteCallback): void {
    this.onAllWavesComplete = callback;
  }

  // Start the next wave
  startNextWave(): boolean {
    if (this.currentWave >= this.totalWaves) {
      return false;
    }

    this.currentWave++;
    this.isWaveActive = true;
    this.enemiesSpawned = 0;
    this.currentSpawnIndex = 0;
    this.spawnTimer = 0;
    this.totalEnemiesInWave = 0;
    this.totalDamageDealt = 0;
    this.enemiesKilled = 0;
    this.enemiesEscaped = 0;

    // Get wave config (use extended generation for waves beyond configured)
    const waveConfig = this.getCurrentWaveConfig();
    if (!waveConfig) {
      this.completeWave();
      return false;
    }

    // Count total enemies for this wave
    this.totalEnemiesInWave = waveConfig.enemySpawns.length;
    this.waveStartDelay = waveConfig.spawnDelay;

    console.log(`[WaveManager] Starting wave ${this.currentWave} with ${this.totalEnemiesInWave} enemies`);

    return true;
  }

  // Main update loop
  update(delta: number): void {
    if (!this.isWaveActive) {
      return;
    }

    // Handle wave start delay
    if (this.waveStartDelay > 0) {
      this.waveStartDelay -= delta;
      return;
    }

    // Get current wave config
    const waveConfig = this.getCurrentWaveConfig();
    if (!waveConfig) {
      this.completeWave();
      return;
    }

    // Spawn enemies
    this.spawnTimer -= delta;
    while (this.spawnTimer <= 0 && this.currentSpawnIndex < waveConfig.enemySpawns.length) {
      const spawn = waveConfig.enemySpawns[this.currentSpawnIndex];
      if (spawn) {
        this.spawnEnemy(spawn, waveConfig.difficultyMultiplier);
        this.enemiesSpawned++;
        this.currentSpawnIndex++;
        this.spawnTimer = waveConfig.timeBetweenSpawns - this.spawnTimer;
      } else {
        break;
      }
    }

    // Check if wave is complete
    if (this.currentSpawnIndex >= this.totalEnemiesInWave && this.activeEnemies.size === 0) {
      this.completeWave();
    }
  }

  private getCurrentWaveConfig(): WaveConfig | undefined {
    return this.currentWave <= this.waveConfigs.length
      ? this.waveConfigs[this.currentWave - 1]
      : generateExtendedWaveConfig(this.currentWave);
  }

  private spawnEnemy(spawn: EnemySpawn, difficultyMultiplier: number): void {
    const config = getEnemyConfigWithDifficulty(spawn.enemyType, difficultyMultiplier);

    // Apply custom properties
    let hp = config.baseHp;
    let speed = config.baseSpeed;
    if (spawn.customProperties) {
      hp *= spawn.customProperties.hpMultiplier || 1;
      speed *= spawn.customProperties.speedMultiplier || 1;
    }

    const enemy: EnemyInstance = {
      id: `enemy_${this.currentWave}_${this.enemiesSpawned}`,
      type: spawn.enemyType,
      maxHp: hp,
      currentHp: hp,
      speed,
      x: 0,
      y: 0,
      pathProgress: 0,
      isDead: false,
      isStealthed: false,
    };

    // Handle stealth special ability
    if (config.specialAbility?.type === 'stealth') {
      enemy.isStealthed = true;
      enemy.abilityState = {
        type: 'stealth',
        isActive: true,
      };
    }

    // Calculate spawn position based on position type
    const spawnPos = this.calculateSpawnPosition(spawn.position);

    // Store enemy
    this.spawnedEnemyIds.push(enemy.id);
    this.activeEnemies.set(enemy.id, enemy);

    // Notify callback
    this.onSpawn?.(enemy, spawnPos.x, spawnPos.y);
  }

  private calculateSpawnPosition(position: 'front' | 'mid' | 'back'): { x: number; y: number } {
    // Spawn area at the start of the path
    const baseY = this.scene.scale.height / 2;
    const offsetY = position === 'front' ? -60 : position === 'back' ? 60 : 0;
    return {
      x: -30,
      y: baseY + offsetY,
    };
  }

  // Register an enemy as active (called when enemy spawns in scene)
  registerActiveEnemy(_id: string): void {
    // Already registered in spawnEnemy
  }

  // Remove enemy from active tracking (called when enemy reaches end)
  enemyEscaped(id: string): void {
    this.activeEnemies.delete(id);
    this.enemiesEscaped++;
    this.checkWaveComplete();
  }

  // Remove enemy from active tracking (called when enemy dies)
  enemyKilled(id: string, damageDealt: number): void {
    const enemy = this.activeEnemies.get(id);
    if (enemy) {
      this.totalDamageDealt += damageDealt;
      this.enemiesKilled++;
      this.totalEnemiesKilled++; // Cumulative count
    }
    this.activeEnemies.delete(id);
    this.checkWaveComplete();
  }

  // Get enemy by ID
  getEnemy(id: string): EnemyInstance | undefined {
    return this.activeEnemies.get(id);
  }

  // Get all active enemies
  getActiveEnemies(): EnemyInstance[] {
    return Array.from(this.activeEnemies.values());
  }

  // Apply damage to enemy
  damageEnemy(id: string, amount: number): { killed: boolean; remainingHp: number } {
    const enemy = this.activeEnemies.get(id);
    if (!enemy) {
      return { killed: false, remainingHp: 0 };
    }

    enemy.currentHp -= amount;
    this.totalDamageDealt += amount;

    // Check for special ability triggers
    this.checkEnemyAbilityTriggers(enemy);

    if (enemy.currentHp <= 0) {
      enemy.currentHp = 0;
      enemy.isDead = true;
      this.enemyKilled(id, amount);
      return { killed: true, remainingHp: 0 };
    }

    return { killed: false, remainingHp: enemy.currentHp };
  }

  private checkEnemyAbilityTriggers(enemy: EnemyInstance): void {
    const config = ENEMY_CONFIGS[enemy.type];
    if (!config || !config.specialAbility) return;

    const hpPercent = enemy.currentHp / enemy.maxHp;
    const triggerPercent = config.specialAbility.triggerHpPercent || 0;

    if (hpPercent <= triggerPercent && !enemy.abilityState?.isActive) {
      switch (config.specialAbility.type) {
        case 'shield':
          enemy.abilityState = {
            type: 'shield',
            isActive: true,
            triggerValue: config.specialAbility.effectValue,
            expiresAt: this.scene.time.now + 5000, // 5 second shield
          };
          break;
        case 'speed_boost':
          enemy.abilityState = {
            type: 'speed_boost',
            isActive: true,
            expiresAt: this.scene.time.now + (config.specialAbility.effectDuration || 3000),
          };
          // Speed boost
          enemy.speed *= 1.5;
          break;
        case 'spawn_clone':
          // Handle clone spawning via callback
          console.log(`[WaveManager] Zero-Day at 50% HP - spawning clones`);
          break;
      }
    }
  }

  private checkWaveComplete(): void {
    if (this.currentSpawnIndex >= this.totalEnemiesInWave && this.activeEnemies.size === 0) {
      this.completeWave();
    }
  }

  private completeWave(): void {
    this.isWaveActive = false;
    console.log(`[WaveManager] Wave ${this.currentWave} complete`);

    const rewards: WaveRewards = {
      flops: this.enemiesKilled * 20 + this.currentWave * 50,
      agi: Math.floor(this.currentWave / 3) * 5,
      reputation: this.enemiesKilled > 0 ? 1 : 0,
    };

    const summary: WaveSummary = {
      waveNumber: this.currentWave,
      enemiesKilled: this.enemiesKilled,
      enemiesEscaped: this.enemiesEscaped,
      totalDamageDealt: this.totalDamageDealt,
      rewards,
    };

    this.onWaveComplete?.(summary);

    // Check if all waves complete
    if (this.currentWave >= this.totalWaves) {
      this.onAllWavesComplete?.();
    }
  }

  // Status getters
  getCurrentWaveNumber(): number {
    return this.currentWave;
  }

  getRemainingWaves(): number {
    return this.totalWaves - this.currentWave;
  }

  isWaveInProgress(): boolean {
    return this.isWaveActive;
  }

  getStatus(): WaveStatus {
    return {
      waveNumber: this.currentWave,
      isActive: this.isWaveActive,
      isComplete: !this.isWaveActive && this.currentWave > 0,
      enemiesSpawned: this.enemiesSpawned,
      enemiesRemaining: this.activeEnemies.size,
      spawnProgress:
        this.totalEnemiesInWave > 0
          ? this.currentSpawnIndex / this.totalEnemiesInWave
          : 1,
    };
  }

  // Get total enemies killed across all waves
  getTotalEnemiesKilled(): number {
    return this.totalEnemiesKilled;
  }

  // Reset for new game
  reset(): void {
    this.currentWave = 0;
    this.isWaveActive = false;
    this.activeEnemies.clear();
    this.spawnedEnemyIds = [];
    this.totalEnemiesKilled = 0;
  }
}
